// Dialog zum Anlegen/Bearbeiten einer Textvorlage (DCP-2228 Folge-Feature)
//
// Der Text kommt aus demselben Editor wie der VA-Beschreibungstext — das ist der Punkt
// der Übung: was hier entsteht, wird später dort eingesetzt, also muss es dieselben
// Auszeichnungsmöglichkeiten haben.
//
// Die Kategorie ist ein Freitextfeld MIT Vorschlagsliste (`<input list>`), kein
// Auswahlfeld. Ein Auswahlfeld würde neue Kategorien unmöglich machen, ein nacktes
// Textfeld würde Tippfehler begünstigen. Die Liste schlägt vor, ohne einzuschränken —
// und sie legt bewusst nichts zusammen: „standard" und „Standard" bleiben zwei
// Kategorien, weil der Fachbereich zwei bewusst getrennte Werte getrennt halten muss.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RichTextEditor } from '@/features/events/RichTextEditor';
import { categoriesOf, isBlankMarkdown } from '@/features/events/event-text-pick';
import { useMe } from '@/hooks/usePermissions';
import { usePartners } from '@/services/event-service';
import { Role, type EventText, type SaveEventTextRequest } from '@/types';

/** Kennwert für „keinem Partner zuordnen" im Auswahlfeld — leerer String ist bei Radix
 *  Select kein gültiger Item-Wert. */
const NO_PARTNER = '__zentral__';

interface Props {
  open: boolean;
  /** null = anlegen; sonst bearbeiten. */
  eventText: EventText | null;
  /** Alle Vorlagen — nur für die Kategorie-Vorschläge. */
  allTexts: readonly EventText[];
  /** Serverfehler (409 der Sperre, 404): erscheint im Dialog, nicht als Toast. */
  saveError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (req: SaveEventTextRequest) => void;
  isSaving?: boolean;
}

export interface EventTextFormValues {
  category: string;
  title: string;
  text: string;
}

const emptyForm = (): EventTextFormValues => ({ category: '', title: '', text: '' });


/** Die Fehler, die diese Prüfung melden kann — als i18n-Schlüssel. */
export type EventTextFormError =
  | 'eventTexts.form.errors.category'
  | 'eventTexts.form.errors.title'
  | 'eventTexts.form.errors.text';

/**
 * Prüft das Formular und gibt einen i18n-Schlüssel zurück, oder null.
 *
 * Spiegelt die Serverregeln, damit der häufige Fehler keine Rundreise braucht. Die
 * HTML-Reinigung wird NICHT gespiegelt: die gehört ins Backend, und ein zweiter
 * Sanitizer im Browser würde beim nächsten Umbau auseinanderlaufen.
 */
export function validateEventTextForm(values: EventTextFormValues): EventTextFormError | null {
  if (values.category.trim().length === 0) return 'eventTexts.form.errors.category';
  if (values.title.trim().length === 0) return 'eventTexts.form.errors.title';
  // isBlankMarkdown statt `=== ''`: ein Whitespace-Rest soll ebenfalls als leer gelten.
  if (isBlankMarkdown(values.text)) return 'eventTexts.form.errors.text';

  return null;
}

/**
 * Darf diese Rolle die Partnerzuordnung ändern — und ist das Feld deshalb sichtbar?
 *
 * Reine Funktion, herausgezogen aus der Komponente, damit die Regel ohne Rendern prüfbar
 * ist (US-207): nur die Zentrale/Admin, sowohl beim Anlegen als auch beim Ändern
 * (29.09.2026 — vorher nur beim Ändern sichtbar; beim Anlegen entscheidet der Server die
 * Zuordnung zwar auch aus dem Kontext des Anlegenden, aber die Zentrale/Admin soll die
 * Zuordnung schon beim Anlegen selbst setzen können, nicht erst in einem zweiten Schritt).
 */
export function canReassignTemplate(role: string | undefined): boolean {
  return role === Role.Zentral || role === Role.Admin;
}

/**
 * Die Partnerzuordnung fürs Speichern, oder `undefined` wenn das Feld gar nicht
 * angezeigt wurde.
 *
 * `undefined` ist hier die sichere Wahl (US-207): würde stattdessen immer ein Wert
 * mitgeschickt, sendete ein Partner, der nur den Titel korrigiert, ungewollt
 * `ownerPartnerKey: null` mit — der Server weist das zu Recht mit 403 ab, weil diese
 * Rolle nicht umordnen darf, und die eigentlich harmlose Titeländerung scheitert mit.
 */
export function reassignmentFor(
  canReassign: boolean,
  ownerPartnerKey: string
): string | null | undefined {
  if (!canReassign) return undefined;
  return ownerPartnerKey === NO_PARTNER ? null : ownerPartnerKey;
}

export function EventTextFormDialog({
  open,
  eventText,
  allTexts,
  saveError,
  onOpenChange,
  onSave,
  isSaving,
}: Props) {
  const { t } = useTranslation('settings');
  const { data: me } = useMe();
  const [form, setForm] = useState<EventTextFormValues>(emptyForm);
  const [error, setError] = useState<EventTextFormError | null>(null);
  // Auswahl im Feld — NO_PARTNER statt leerer Text, siehe Konstante oben.
  const [ownerPartnerKey, setOwnerPartnerKey] = useState<string>(NO_PARTNER);

  const isEdit = eventText !== null;
  const categories = useMemo(() => categoriesOf(allTexts), [allTexts]);
  const canReassign = canReassignTemplate(me?.role);
  const { data: partners = [] } = usePartners(canReassign);

  useEffect(() => {
    if (!open) return;
    setForm(
      eventText
        ? { category: eventText.category, title: eventText.title, text: eventText.text }
        : emptyForm()
    );
    setOwnerPartnerKey(eventText?.ownerPartnerKey ?? NO_PARTNER);
    setError(null);
  }, [open, eventText]);

  const handleSubmit = () => {
    const problem = validateEventTextForm(form);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    onSave({
      id: eventText?.id,
      category: form.category.trim(),
      title: form.title.trim(),
      text: form.text,
      // Fassungsstand mitschicken: passt er nicht mehr, antwortet der Server mit 409
      // statt den Text eines anderen zu überschreiben.
      etag: eventText?.etag,
      ownerPartnerKey: reassignmentFor(canReassign, ownerPartnerKey),
    });
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Breiter als die anderen Dialoge: hier steht ein Editor drin, kein Formularfeld. */}
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? 'eventTexts.form.titleEdit' : 'eventTexts.form.titleCreate')}
          </DialogTitle>
          <DialogDescription>{t('eventTexts.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="event-text-category">
                {t('eventTexts.form.fields.category')}
              </label>
              {/* Freitext mit Vorschlägen: neue Kategorien bleiben möglich, der
                  Tippfehler wird unwahrscheinlicher. */}
              <Input
                id="event-text-category"
                list="event-text-categories"
                value={form.category}
                onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                placeholder="Verkostung"
              />
              <datalist id="event-text-categories">
                {categories.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
              <p className="text-xs text-muted-foreground">
                {t('eventTexts.form.categoryHint')}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="event-text-title">
                {t('eventTexts.form.fields.title')}
              </label>
              <Input
                id="event-text-title"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Verkostung (Standard)"
              />
            </div>
          </div>

          {/* Nur die Zentrale sieht dieses Feld (US-207). Ein Partner darf seine eigene
              Vorlage bearbeiten, aber nicht umordnen — auch nicht in der eigenen. */}
          {canReassign && (
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="event-text-owner">
                {t('eventTexts.form.fields.owner')}
              </label>
              <Select value={ownerPartnerKey} onValueChange={setOwnerPartnerKey}>
                <SelectTrigger id="event-text-owner">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PARTNER}>{t('eventTexts.form.ownerCentral')}</SelectItem>
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.key}>
                      {partner.key}
                      {partner.email ? ` — ${partner.email}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t('eventTexts.form.ownerHint')}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t('eventTexts.form.fields.text')}
            </label>
            <RichTextEditor
              value={form.text}
              onChange={(html) => setForm((prev) => ({ ...prev, text: html }))}
            />
          </div>

          {/* Eigene Prüfung zuerst, dann die Servermeldung — die ist spezifischer
              (Sperre, gelöschte Vorlage) und soll nicht verdeckt werden. */}
          {error && <p className="text-sm text-red-600">{t(error)}</p>}
          {!error && saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('eventTexts.form.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? t('eventTexts.form.saving') : t('eventTexts.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
